import React from 'react';
import { Mail, MapPin, Facebook, Instagram, Youtube } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import FooterSubscription from './FooterSubscription';

const Footer = () => {
  const navigate = useNavigate();

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Statement of Faith', href: '/statement-of-faith' },
    { name: 'Prayer Requests', href: '/prayer' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' }
  ];

  const ministries = [
    { name: 'Online Sermons', href: '/sermons' },
    { name: 'Bible Studies', href: '/bible-studies' },
    { name: 'Prayer Support', href: '/prayer' },
    { name: 'Gospel Outreach', href: '/outreach' },
    { name: 'Community Outreach', href: '/outreach' }
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/elbethelglobal', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/elbethelglobal', label: 'Instagram' },
    { icon: Youtube, href: 'https://www.youtube.com/@ElBethelGlobal', label: 'YouTube' }
  ];

  const handleNavigation = (href) => {
    if (href.startsWith('/')) {
      navigate(href);
    }
  };

  return (
    <footer id="contact" className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12 lg:py-16">
        {/* Newsletter Subscription */}
        <div className="mb-12">
          <FooterSubscription />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8 lg:mb-12">
          {/* Ministry Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_elbethel-worship/artifacts/6rez83rp_El%20bethel%20Transparent%20Logo.png"
                alt="El Bethel Ministries"
                className="w-10 lg:w-12 h-10 lg:h-12"
              />
              <div>
                <div className="font-bold text-base lg:text-lg font-display">EL BETHEL</div>
                <div className="font-bold text-base lg:text-lg font-display">MINISTRIES</div>
              </div>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed font-sans text-sm lg:text-base">
              A community rooted in God's Word, providing biblical teaching, prayer support, and gospel outreach. 
              Founded by Sean and Rhoda Talluri.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a 
                    key={index}
                    href={social.href}
                    target={social.href.startsWith('http') ? '_blank' : '_self'}
                    rel={social.href.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="w-10 h-10 bg-[#f17b21] hover:bg-[#d86919] rounded-full flex items-center justify-center transition-colors duration-300"
                    aria-label={social.label}
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 font-display">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button 
                    onClick={() => handleNavigation(link.href)}
                    className="text-gray-300 hover:text-[#f17b21] transition-colors duration-300 font-sans text-left"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Ministries */}
          <div>
            <h3 className="text-lg font-bold mb-6 font-display">Ministries</h3>
            <ul className="space-y-3">
              {ministries.map((ministry, index) => (
                <li key={index}>
                  <button 
                    onClick={() => handleNavigation(ministry.href)}
                    className="text-gray-300 hover:text-[#f17b21] transition-colors duration-300 font-sans text-left"
                  >
                    {ministry.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 font-display">Contact Us</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#f17b21] flex-shrink-0 mt-1" />
                <div className="text-gray-300 font-sans text-sm lg:text-base">
                  <p>1910 Pacific Ave</p>
                  <p>Ste 2000-1947</p>
                  <p>Dallas, TX 75201</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-[#f17b21] flex-shrink-0" />
                <a 
                  href="mailto:contact@elbethel.online"
                  className="text-gray-300 hover:text-[#f17b21] transition-colors duration-300 font-sans text-sm lg:text-base"
                >
                  contact@elbethel.online
                </a>
              </div>
            </div>

            <div className="mt-8">
              <h4 className="font-semibold mb-4 font-display">Prayer Support</h4>
              <p className="text-gray-300 text-sm mb-4 font-sans">
                Submit a prayer request and our team will pray over your needs.
              </p>
              <button 
                onClick={() => navigate('/prayer')}
                className="bg-[#f17b21] hover:bg-[#d86919] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-300 font-sans"
              >
                Submit Prayer Request
              </button>
            </div>
          </div>
        </div>

        {/* Connected Ministries */}
        <div className="border-t border-gray-800 pt-6 lg:pt-8 mb-6 lg:mb-8">
          <h3 className="text-lg font-bold mb-4 text-center font-display">Connected Ministries</h3>
          <div className="flex flex-wrap justify-center gap-6">
            <a
              href="https://theblanket.us"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-gray-300 hover:text-[#f17b21] transition-colors duration-300 font-sans"
            >
              <span>The Blanket Project</span>
            </a>
            <a
              href="https://christeverywhere.org"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-gray-300 hover:text-[#f17b21] transition-colors duration-300 font-sans"
            >
              <span>Christ Everywhere</span>
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-6 lg:pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0 font-sans">
              © 2024 El Bethel Ministries. All rights reserved. Founded by Sean & Rhoda Talluri.
            </div>
            <div className="flex space-x-6 text-sm text-gray-400">
              <button 
                onClick={() => navigate('/privacy')}
                className="hover:text-[#f17b21] transition-colors duration-300 font-sans"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/terms')}
                className="hover:text-[#f17b21] transition-colors duration-300 font-sans"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => navigate('/statement-of-faith')}
                className="hover:text-[#f17b21] transition-colors duration-300 font-sans"
              >
                Statement of Faith
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;